import React from "react";
import { Clock, Heart } from "lucide-react";

const formatMoney = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const getDaysRemaining = (deal) => {
  const expiryDate =
    deal.expiry_date || deal.end_date || deal.validUntil || deal.expiryDate;
  if (!expiryDate) return null;

  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return null;

  expiry.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const getDealMetaLabel = (deal) => {
  const daysLeft = getDaysRemaining(deal);
  if (daysLeft != null) {
    if (daysLeft === 0) return "Ends today";
    if (daysLeft === 1) return "1 day left";
    return `${daysLeft} days left`;
  }

  const category = deal.category || deal.deal_category || deal.brand_category;
  if (category) return category;

  return "Active deal";
};

const StoreDealCard = ({ deal, compact = false }) => {
  const discountedPrice = deal.discountedPrice ?? deal.discounted_price;
  const price = deal.price;
  const image = deal.image ?? deal.deal_image ?? deal.company_logo;

  const discount =
    deal.discountPercentage ??
    (price && discountedPrice
      ? Math.round(((price - discountedPrice) / price) * 100)
      : null);

  const metaLabel = getDealMetaLabel(deal);

  return (
    <div className="flex flex-col h-full bg-white border border-gray-100 rounded-sm overflow-hidden pointer-events-none select-none">
      {/* Product image */}
      <div className="relative flex items-center justify-center bg-white aspect-square p-2 sm:p-3">
        {image ? (
          <img
            src={image}
            alt={deal.title}
            className="object-contain w-full h-full max-h-[140px] sm:max-h-[160px]"
          />
        ) : (
          <div className="w-full h-full bg-gray-50 rounded" />
        )}
        <span className="absolute bottom-2 right-2 text-[#FF6C6C]">
          <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.5} />
        </span>
      </div>

      {/* Product info */}
      <div className={`flex flex-col flex-1 ${compact ? "px-2 pb-2 pt-1" : "px-2.5 pb-2.5 pt-1.5"}`}>
        <span className="inline-block w-fit px-1.5 py-0.5 mb-1.5 text-[8px] font-bold text-white bg-[#1E6FD9] rounded-sm sm:text-[9px]">
          Official Store
        </span>

        <h3
          className="mb-1.5 text-[10px] font-normal leading-snug text-gray-800 line-clamp-2 sm:text-[11px]"
          title={deal.title}
        >
          {deal.title}
        </h3>

        {discountedPrice != null ? (
          <p className="text-sm font-bold leading-none text-gray-900 sm:text-base">
            ₵ {formatMoney(discountedPrice)}
          </p>
        ) : null}

        {price && discountedPrice && discount ? (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            <span className="text-[10px] text-gray-400 line-through sm:text-[11px]">
              ₵ {formatMoney(price)}
            </span>
            <span className="text-[10px] font-medium text-[#FF6C6C] sm:text-[11px]">
              -{discount}%
            </span>
          </div>
        ) : null}

        <div className="flex items-center gap-1 mt-1.5 min-w-0">
          <Clock className="w-3 h-3 shrink-0 text-gray-400" />
          <span className="text-[9px] text-gray-500 truncate sm:text-[10px]">{metaLabel}</span>
        </div>

        <div className="mt-auto pt-2">
          <span className="inline-flex items-center text-[8px] font-extrabold tracking-tight sm:text-[9px]">
            <span className="text-[#FF6C6C]">DEAL</span>
            <span className="text-gray-800">BOXX</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default StoreDealCard;
